
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, OnInit, HostListener, Input, ViewChild, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { AuthService } from "../../../common/providers/auth.service";
import { ConfigService } from "../../../common/providers/config.service";
import { HeaderService, HeaderEvent } from "../../services/header.service";
import { Subject } from "rxjs";
import { ResponsiveService } from "../../../common/providers/responsive.service";
import { Store } from "@ngrx/store";
import { TasksRootState, getAuthUser, getIsSearchMode, SearchModeToggle, getSelectedFilterType, getTasksIsLoading, SetDetailView } from "../../store/index";
import { environment } from "../../../../environments/environment";
import { Environment } from "../../../../environments/environment.interface";
import { CommonUtil } from "../../../common/utils/common.utils";
import { TaskService } from "../../task.service";
import { TasksConstants } from "../../shared/task-constacts";
import { MessageTranslatorService } from "../../services/message-translator-service";
import { ErrorService } from "../../../common/providers/error-service";
import { ErrorType } from "../../shared/task-enum";
import { Broadcaster } from "../../../common/providers/broadcaster.service";
import { SetActiveProfile, SetRightSidebarStatus } from "../../../actions/app";
import { AdvanceSearchRequest, TimeRangeType, DueDateTimeRangeType } from "../../models";
import { SearchRepository } from "../../repository/search.repository";
import { MdlDialogService } from "@angular-mdl/core";
import { TaskAboutusDialogComponent } from "../task-settings/task-aboutus-dialog/task-aboutus-dialog.component";
import { TaskChangelogDialogComponent } from "../task-settings/task-changelog-dialog/task-changelog-dialog.component";
import { TaskGeneralSettingsDialogComponent } from "../task-settings/task-general-settings-dialog/task-general-settings.component";
import { TaskServiceDeskDialogComponent } from "../task-settings/task-service-desk-dialog/task-service-desk-dialog.component";
import { TaskHelpAndFaqDialogComponent } from "../task-settings/task-help-faq-dialog/task-help-faq-dialog.component";
import { TaskLoginProfileDialogComponent } from "../task-login-user-profile/task-login-user-profile.component";
import { TaskRepository } from "../../repository/task.repository";
import { getIsRightSidebarExpanded, getOnlineStatus, getFederatedApps } from "../../../reducers";
import { TaskLegalNoticeDialogComponent } from "../task-settings/task-legal-notice-dialog/task-legal-notice-dialog.component";
import { getIsDetailView } from "../../store/selectors";
import { ResetSelectedTaskId, SetSelectAll } from "../../store/actions";
import { UsersManagementComponent } from "../sidebar/users-management.component";
import { ArchiveUsersComponent } from "../sidebar/archive-users.components";
import { SaveSearchDialogComponent } from "../save-search-dialog/save-search-dialog.component";
import { takeWhile, take, takeUntil } from "rxjs/operators";
import { ChangePasswordFormComponent } from "../change-password/change-password-form/change-password-form.component";
import { MatDialog } from "@angular/material/dialog";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";

@Component({
  selector: "vp-header",
  templateUrl: "./header.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class HeaderComponent implements OnInit, OnDestroy {
  environment: Environment = environment;
  showMenu: boolean = false;
  user: any = {};
  userImage: string = "";
  switchApp: boolean = false;
  appLogo: string = "";
  search: string = "";
  showSettings: boolean = false;
  currentUser: any = {};
  breadcrumbs: any[] = [];
  @Input() url: string;
  showAdvancedSearchPopover: boolean = false;
  showAppSwitcher: boolean = true;
  destroy$ = new Subject();
  private isAlive$ = new Subject<boolean>();
  isAlive = true;
  screen: string = this.responsiveService.getScreen();
  advanceSearch: AdvanceSearchRequest;
  keyword: string;
  selectedFilterType: string;
  taskConstants = TasksConstants;
  isMobileDevice : boolean = false;
  isLoading = false;
  isSearchMode: boolean = false;
  isOnline: boolean = false;
  isDetailView: boolean = false;
  isCordovaOrElectron = environment.isCordova || environment.isElectron;
  rightSidebarExpanded:boolean = false;
  ActiveSearchBar: boolean = false;
  isFocused: boolean = false;
  storedLanguage: string;
  logoUrlBackend: string;
  faqURL: string;
  userManual: string;
  termsOfUse: string;
  dataPrivacy: string;
  imprintLink: string;
  constructor(
    private router: Router,
    private activated: ActivatedRoute,
    private config: ConfigService,
    private authService: AuthService,
    private taskService: TaskService,
    private responsiveService: ResponsiveService,
    private headerService: HeaderService,
    private store: Store<TasksRootState>,
    private searchRepo: SearchRepository,
    private taskRepo: TaskRepository,
    private errorService: ErrorService,
    private broadcaster: Broadcaster,
    private messageTranslatorService: MessageTranslatorService,
    private changerDetectorRef: ChangeDetectorRef,
    private dialogService: MdlDialogService,
    private breakpointObserver: BreakpointObserver,
    private matDialog: MatDialog ) {
    this.storedLanguage = localStorage.getItem(TasksConstants.TASK_LANGUAGE);
    this.showAppSwitcher = (this.config.get("appsEnabled") || [])
      .filter(app => app.enabled).length > 1;

    this.store.select(getFederatedApps).pipe(takeWhile(() => this.isAlive)).subscribe(apps => {
      const vnctaskapp = apps.find(v => v.name === "vnctask");
      if (vnctaskapp && vnctaskapp.options?.logotype) {
        this.logoUrlBackend = this.config.get("publicVncDirectoryUrl") + vnctaskapp.options.logotype;
        this.changerDetectorRef.markForCheck();
      }
    });

    this.config.currentLanguage.subscribe((lang: any) => {
      this.storedLanguage = lang;
      this.setConfigUrl();
    });
    this.store.select(getAuthUser).pipe(takeWhile(() => this.isAlive)).subscribe(user => {
      if (user) {
        this.currentUser = user;
        console.log("currentUser user", user);
        this.taskRepo.addAllTaskToIndexedDb();
        this.changerDetectorRef.markForCheck();
      }
    });
    this.isMobileDevice = this.breakpointObserver.isMatched("(max-width: 767px)");
    this.breakpointObserver.observe(["(max-width: 767px)"]).pipe(takeUntil(this.isAlive$)).subscribe((state: BreakpointState) => {
      if (state.matches) {
        this.isMobileDevice = true;
      } else {
        this.isMobileDevice = false;
      }
      this.changerDetectorRef.markForCheck();
    });
  }

  ngOnInit() {
    this.setConfigUrl();
    this.searchRepo.selectAdvanceSearchRequest().pipe(takeUntil(this.isAlive$)).subscribe((request) => {
      this.advanceSearch = request;
      setTimeout(() => {
        this.changerDetectorRef.markForCheck();
      }, 0);
    });

    this.searchRepo.getSearchKeyword().pipe(takeUntil(this.isAlive$)).subscribe((request) => {
      this.keyword = request;
      setTimeout(() => {
        this.changerDetectorRef.markForCheck();
      }, 0);
    });

    this.store.select(getOnlineStatus).pipe(takeUntil(this.isAlive$)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      this.changerDetectorRef.markForCheck();
    });

    this.store.select(getIsDetailView).pipe(takeWhile(() => this.isAlive)).subscribe( value => {
      this.isDetailView = value;
      this.changerDetectorRef.markForCheck();
    });
    this.store.select(getIsRightSidebarExpanded).pipe(takeWhile(() => this.isAlive))
      .subscribe(res => {
      this.rightSidebarExpanded = res;
      this.changerDetectorRef.markForCheck();
    });

    this.headerService
      .searchText$
      .pipe(takeUntil(this.destroy$))
      .subscribe((text: string) => {
        this.search = text;
        this.changerDetectorRef.markForCheck();
      });

    this.responsiveService.screen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(screen => {
        this.screen = screen;
        this.changerDetectorRef.markForCheck();
      });

    this.headerService.getSearchEmitter()
      .subscribe((data: HeaderEvent) => {
        if (data.action === HeaderEvent.ENTER) {
          if (data.text) {
            if (this.isOnline || CommonUtil.isSQLSupported()) {
              this.searchRepo.saveSearchKeyword(data.text);
              let query = this.searchRepo.getSearchQuery(data.text, this.advanceSearch);
              this.taskRepo.setSearchParams(query);

              // if already in search mode - then just show tasks
              if (this.isSearchMode) {
                if (this.isDetailView) {
                  this.store.dispatch(new SetSelectAll(false));
                  this.store.dispatch(new ResetSelectedTaskId());
                  this.store.dispatch(new SetDetailView(false));
                  this.router.navigate(["./", TasksConstants.ROUTE_SEARCH], { relativeTo: this.activated });
                }
                this.taskRepo.getTasks();
                this.taskRepo.getTasksStats();
              } else {
                this.router.navigate(["/task", TasksConstants.ROUTE_SEARCH], { queryParams: {} });
              }
            } else {
              this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
            }
          } else {
            this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.messageTranslatorService.getMessage(TasksConstants.SEARCH_STRING_ERR_MSG) });
          }
        }
      });

    this.store.select(getIsSearchMode).pipe(takeWhile(() => this.isAlive)).subscribe((value) => {
      if (!value) {
        this.search = "";
      }
      this.isSearchMode = value;
      this.changerDetectorRef.markForCheck();
    });

    this.store
      .select(getSelectedFilterType)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(value => {
        this.selectedFilterType = value;
        this.changerDetectorRef.markForCheck();
      });

    this.store.select(getTasksIsLoading).pipe(takeWhile(() => this.isAlive)).subscribe((value) => {
      this.isLoading = value;
      this.changerDetectorRef.markForCheck();
    });
  }

  toggleSidebar() {
    if (this.rightSidebarExpanded) {
      this.store.dispatch(new SetActiveProfile(null));
      this.store.dispatch(new SetRightSidebarStatus(false));
    }
    else {
      this.store.dispatch(new SetActiveProfile(this.currentUser?.email));
      this.store.dispatch(new SetRightSidebarStatus(true));
    }
  }

  showDropDown($event, name: string): void {
    $event.isSettings = true;
    this[name] = !this[name];
    this.changerDetectorRef.markForCheck();
  }

  logout(): void {
    if (environment.isCordova && typeof window.FirebasePlugin !== "undefined") {
      window.FirebasePlugin.getToken(token => {
        this.taskService.removeFirebaseToken(token)
          .pipe(take(1))
          .subscribe(res => {
            console.log("[removeFirebaseToken]", token, res);
            window.FirebasePlugin.unregister();
            this.processLogout();
          }, err => {
            console.error("[removeFirebaseToken - error]", err);
            this.processLogout();
          });
      });

      window.FirebasePlugin.clearAllNotifications(() => {
        console.log("[FirebasePlugin clearAllNotifications] success");
      }, error => {
        console.log("[FirebasePlugin clearAllNotifications] error", error);
      });
    } else {
      this.processLogout();
    }
  }

  private processLogout(): void {
    console.log("[header.component] logout");
    this.taskRepo.logout();
  }

  searchTextUpdated(key: number): void {
    this.headerService.emit(key, this.search);
  }

  navigate(path: string[]): void {
    this.router.navigate(path);
  }

  emitAction(data: { event: any, action: string }) {
    data.event.stopPropagation();
    // this.headerService.emitAction(data.action);
    switch (data.action) {
      case HeaderService.ACTIONS.TASK.TASK_SEARCH:
        this.headerService.showSearchDialog$.next(true);
        break;
    }
    this.showSettings = false;
    this.changerDetectorRef.markForCheck();
  }

  switchApplication(event): void {
    if (event && event.switch) {
      this.switchApp = !this.switchApp;
    } else {
      this.switchApp = false;
    }
    this.changerDetectorRef.markForCheck();
  }

  @HostListener("document:click", ["$event"])
  onClickOutside($event): void {
    if (!($event.isSettings)) {
      this.showSettings = false;
      this.changerDetectorRef.markForCheck();
    }
  }

  toggleDrawer(value: boolean): void {
    this.headerService.toggleDrawer(value);
  }

  hideSettings(): void {
    this.showSettings = false;
    this.changerDetectorRef.markForCheck();
  }

  ngOnDestroy() {
    this.isAlive = false;
    this.isAlive$.next(false);
    this.isAlive$.complete();
    this.headerService.destroy();
  }

  showAdvancedTaskFilters() {
    if (this.isOnline) {
      this.headerService.showAdvancedTaskFilters$.next(true);
    } else {
      this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
    }
  }

  logoUrl() {
    return CommonUtil.getFullUrl("/assets/img/logo.png");
  }

  openAboutDialog(): void {
    const gdlg = this.matDialog.open(TaskAboutusDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vnctaskaboutdialog",
    });
  }

  openChangeLogDialog(): void {
    const gdlg = this.matDialog.open(TaskChangelogDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vnctaskchangelogdialog",
    });
  }

  openHelpFaqDialog(): void {
    const gdlg = this.matDialog.open(TaskHelpAndFaqDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vnctaskhelpfaqdialog",
    });
  }

  openServiceDeskDialog(): void {
    const aDialog = this.matDialog.open(TaskServiceDeskDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vnctaskservicedialog",
    });
  }

  openGeneralSettingDialog(): void {
    const gdlg = this.matDialog.open(TaskGeneralSettingsDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vnctaskgeneralsettingsdialog",
    });
  }

  openProfileSidebar() {
    this.store.dispatch(new SetRightSidebarStatus(true));
    this.store.dispatch(new SetActiveProfile(this.currentUser?.email));
  }

  openLegalNoticeDialog() {
    const gdlg = this.matDialog.open(TaskLegalNoticeDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vnctasksettingsdialog",
    });
  }

  openAppFaq() {
    CommonUtil.openUrl(this.faqURL);
  }

  openUserManual() {
    CommonUtil.openUrl(this.userManual);
  }

  openTermOfUse() {
    CommonUtil.openUrl(this.termsOfUse);
  }

  openPrivacyPolicy() {
    CommonUtil.openUrl(this.dataPrivacy);
  }

  openImprint() {
    CommonUtil.openUrl(this.imprintLink);
  }

  onClickReloadTasks() {
    this.taskRepo.reloadApp();
  }

  openInviteUserDialog() {
    let dialogStyle: any = {
      visibility: "visible",
      "width": "99%",
      "max-width": "400px",
      "min-height": "400px"
    };
    this.dialogService.showCustomDialog({
      component: UsersManagementComponent,
      providers: [],
      isModal: true,
      styles: dialogStyle,
      animate: false,
      clickOutsideToClose: true,
      classes: ""
    });
  }

  openArchiveUserDialog() {
    let dialogStyle: any = {
      visibility: "visible",
      "width": "99%",
      "max-width": "400px",
      "min-height": "400px"
    };
    this.dialogService.showCustomDialog({
      component: ArchiveUsersComponent,
      providers: [],
      isModal: true,
      styles: dialogStyle,
      animate: false,
      clickOutsideToClose: true,
      classes: ""
    });
  }

  openSaveSearchDialog() {
    const sDialog = this.matDialog.open(SaveSearchDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vp-save-search-dialog",
    });
    sDialog.afterClosed().pipe(take(1)).subscribe($event => {
      if ($event && $event.query) {
        this.navigateSaveSearches($event.query);
      }
    });
  }

  navigateSaveSearches(query) {
    this.taskRepo.setSearchParams(query);
    // if already in search mode - then just show tasks
    if (this.isSearchMode) {
      if (this.isDetailView) {
        this.store.dispatch(new SetSelectAll(false));
        this.store.dispatch(new ResetSelectedTaskId());
        this.store.dispatch(new SetDetailView(false));
        this.router.navigate(["./", TasksConstants.ROUTE_SEARCH], { relativeTo: this.activated });
      }
      this.taskRepo.getTasks();
      this.taskRepo.getTasksStats();
    } else {
      this.router.navigate(["/task", TasksConstants.ROUTE_SEARCH], { queryParams: {} });
    }
  }

  changePassword() {
    if (this.config.get("isChangePasswordViaVNCd") && this.config.get("redirectPasswordChangeUrl")) {
      CommonUtil.openUrl(this.config.get("redirectPasswordChangeUrl"));
      return;
    }

    if (this.isOnline) {
      const sDialog = this.matDialog.open(ChangePasswordFormComponent, {
        maxWidth: "100%",
        autoFocus: false,
        panelClass: "vp-task-change-password-dialog",
      });
      sDialog.afterClosed().pipe(take(1)).subscribe($event => {
        if ($event) {
          this.logout();
        }
      });
    } else {
      this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
    }
  }

  searchFocus() {
    this.ActiveSearchBar = true;
  }

  handleSearchTextFieldBlur(event: FocusEvent) {
    setTimeout(() => {
        this.isFocused = false;
    }, 200);
    this.ActiveSearchBar = false;
  }

  setConfigUrl() {
    if (this.storedLanguage === "de") {
      if (this.config.URLS.FAQ_DE) {
        this.faqURL = this.config.URLS.FAQ_DE;
      } else {
        this.faqURL = "https://de.docs.vnc.biz/VNCtask/faq/";
      }
      if (this.config.URLS.TERMS_OF_USE_URL_DE) {
        this.termsOfUse = this.config.URLS.TERMS_OF_USE_URL_DE;
      } else {
        this.termsOfUse = "https://vnclagoon.com/de/Nutzungsbedingungen/";
      }

      if (this.config.URLS.DATA_PRIVACY_URL_DE) {
        this.dataPrivacy = this.config.URLS.DATA_PRIVACY_URL_DE;
      } else {
        this.dataPrivacy = "https://vnclagoon.com/de/datenschutzerklaerung/";
      }

      if (this.config.URLS.IMPRINT_URL_DE) {
        this.imprintLink = this.config.URLS.IMPRINT_URL_DE;
      } else {
        this.imprintLink = "https://vnclagoon.com/de/uber-uns/rechtsinformationen/";
      }

      if (this.config.URLS.USER_MANUAL_DE) {
        this.userManual = this.config.URLS.USER_MANUAL_DE;
      } else {
        this.userManual = "https://de.docs.vnc.biz/VNCtask/browsermanual/";
      }
    } else {
      if (this.config.URLS.FAQ) {
        this.faqURL = this.config.URLS.FAQ;
      } else {
        this.faqURL = "https://en.docs.vnc.biz/VNCtask/faq/";
      }
      if (this.config.URLS.TERMS_OF_USE_URL) {
        this.termsOfUse = this.config.URLS.TERMS_OF_USE_URL;
      } else {
        this.termsOfUse = "https://vnclagoon.com/terms";
      }

      if (this.config.URLS.DATA_PRIVACY_URL) {
        this.dataPrivacy = this.config.URLS.DATA_PRIVACY_URL;
      } else {
        this.dataPrivacy = "https://vnclagoon.com/data-privacy-policy/";
      }
      if (this.config.URLS.IMPRINT_URL) {
        this.imprintLink = this.config.URLS.IMPRINT_URL;
      } else {
        this.imprintLink = "https://vnclagoon.com/about-vnc/legal-information/";
      }
      if (this.config.URLS.USER_MANUAL) {
        this.userManual = this.config.URLS.USER_MANUAL;
      } else {
        this.userManual = "https://en.docs.vnc.biz/VNCtask/overview/";
      }
    }
  }
}
